"use client";
import { Products } from "@/app/enums/Products";
import { getCookie, setCrossDomainCookie } from "@/app/utils/client-cookies";
import { generateSiteToken } from "@/app/utils/loan/loan-utils";
import { throwIfCallbackAlreadyPlanned } from "@/app/endpoints/commonEndpoints";
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";

const BASE_API = process.env.NEXT_PUBLIC_RM_BASE_API + "/api";
const BASE_WEBSITE = process.env.NEXT_PUBLIC_RM_BASE_API;

export interface LoanLandingStoreState {
    token?: string;
    currentQuote: {};
    isInitialLoading: boolean;
    callbackForm: {
        lastName?: string;
        firstName?: string;
        email?: string;
        phone?: string;
        date?: string;
        hours?: number;
        acceptPhoneContact?: boolean;
        acceptEmailContact?: boolean;
        legalConsent?: boolean;
        // kill switch: when false, the callback payload carries no consent
        phoneConsentMode?: boolean;
    };
    isCallbackFormLoading: boolean;
    setProperty: Function;
    setToken: Function;
    askForAppointment: Function;
    createQuote: Function;
}

export const useLoanLandingStore = create<LoanLandingStoreState>()(
    immer((set, get) => {
        return {
            token: undefined,
            isInitialLoading: true,
            callbackForm: {
                lastName: undefined,
                firstName: undefined,
                email: undefined,
                phone: undefined,
                date: new Date().toISOString().split("T")[0],
                hours: 1000,
                acceptPhoneContact: false,
                acceptEmailContact: false,
                legalConsent: false,
                phoneConsentMode: true,
            },
            isCallbackFormLoading: false,
            currentQuote: {},

            createQuote: async (askForAppointment = false) => {
                const { token, callbackForm } = get();

                const formData = {
                    email: callbackForm.email as string,
                    firstname: callbackForm.firstName as string,
                    lastname: callbackForm.lastName as string,
                    phone: callbackForm.phone as string,
                    utmTrackingToken: getCookie("rm_user_token") || getCookie("utm_tracking_token") || null,
                };

                set((state) => {
                    state.isCallbackFormLoading = true;
                });

                return fetch(`${BASE_API}/loan-comparator/create-quote-from-landing`, {
                    body: JSON.stringify(formData),
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "X-Site-Token": generateSiteToken("quote_creation"),
                    },
                    credentials: "include",
                })
                    .then(async (response) => {
                        const data = await response.json();

                        set((state) => {
                            state.token = data.token;
                        });

                        // Set the loan_quote_stored_token cookie
                        setCrossDomainCookie("loan_quote_stored_token", data.token);

                        if (askForAppointment) {
                            return get().askForAppointment();
                        }

                        return response.status === 200;
                    })
                    .finally(() => {
                        set((state) => {
                            state.isCallbackFormLoading = false;
                        });
                    });
            },

            askForAppointment: async () => {
                const { callbackForm, token } = get();

                const phoneToPlus33 = callbackForm.phone?.replace(/^0/, "+33");

                const formData = {
                    token: token as string,
                    appointment_date: new Date(callbackForm.date as string),
                    appointment_time: (callbackForm.hours || "").toString(),
                    first_name: callbackForm.firstName as string,
                    last_name: callbackForm.lastName as string,
                    phone: phoneToPlus33 as string,
                    email: callbackForm.email as string,
                    product_type: Products.LOAN,
                    type: 11,
                    from_link: window.location.href,
                    // Consent mode off: nothing was collected, so no consent is written
                    ...(callbackForm.phoneConsentMode === false ? {} : {
                        accept_phone_contact: callbackForm.acceptPhoneContact === true,
                        accept_email_contact: callbackForm.acceptEmailContact === true,
                    }),
                };

                set((state) => {
                    state.isCallbackFormLoading = true;
                });

                try {
                    const response = await fetch(`${BASE_API}/common/callback`, {
                        body: JSON.stringify(formData),
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        credentials: "include",
                    });

                    await throwIfCallbackAlreadyPlanned(response);

                    return response.status === 200;
                } finally {
                    set((state) => {
                        state.isCallbackFormLoading = false;
                    });
                }
            },

            setProperty: (property: keyof LoanLandingStoreState["callbackForm"], value: any) => {
                set((state) => {
                    state.callbackForm[property] = value;
                });
            },

            setToken: (token: string) => {
                set((state) => {
                    state.token = token;
                });
            },
        } as LoanLandingStoreState;
    })
);
